// ────────────────────────────────────────────────────────────────────
// The staff asset screen — design_handoff/README.md:152-219, "the centre of
// the system". This file is the ASSEMBLY: two columns in a `position: relative`
// row (the raw-file modal anchors to it), the 254px client-grouped sidebar on
// the left and the scrolling detail column on the right.
//
// It composes and it does not compute. Every number below arrives from
// `GET /api/assets/{id}/integrity` (./types.ts) already made, carrying the
// sentence that explains it; the four identity facts the record does not hold
// — in-service date, geometry, nominal wall, CML count — come off the asset row
// (`AssetIdentityFacts` is a structural subset of `Asset` for exactly that).
// Nothing here derives a rate, halves an interval, builds a due date or decides
// which rate governs. Those live in server/integrity/rules.py, they carry a
// tolerance and a month floor, and a second copy of them in the browser would
// drift from the first without any test noticing.
//
// THE INSPECTION LIST IS NEWEST FIRST. Nothing here reads `inspections[0]`.
// The current inspection is asked for by name (./order), because reading the
// wrong end of this array is a defect this screen has already shipped once.
// ────────────────────────────────────────────────────────────────────
import { useState } from "react";
import { RemainingLifeProjection, type AssetThicknessSeries } from "@/components/RemainingLifeProjection";
import { AssetIdentityBlock, type AssetIdentityFacts } from "./AssetIdentityBlock";
import { AssetBottomCards, type AssetRecommendationRow, type AssetRepairRow } from "./BottomCards";
import { AssetSidebar, type SidebarAsset, type SidebarDue } from "./AssetSidebar";
import { ComponentsTable } from "./ComponentsTable";
import { HistoryExplainer } from "./HistoryExplainer";
import { InspectionDetailPanel, type FileLabel } from "./InspectionDetailPanel";
import { InspectionHistoryTable } from "./InspectionHistoryTable";
import { RateStrip } from "./RateStrip";
import { RawFileModal, type RawFileKind } from "./RawFileModal";
import { currentEvent, oldestFirst } from "./order";
import type { AssetIntegrity } from "./types";

/** One filed file, as `GET /api/intake?asset_id=` returns it. A subset of
 *  `lib/types.ts` IntakeFile — the integrity record carries `file_ids` and
 *  nothing else, so the names, kinds and download links come from the file
 *  store. */
export interface AssetFileRow {
  id: number;
  filename: string;
  /** IntakeKind — photo | report | data | drawing | other. */
  kind: string;
  download_url: string | null;
}

/** The intake vocabulary mapped onto the modal's body types.
 *
 *  A mapping, not an inference about the contents: the modal chooses which
 *  BODY to draw, and every body it can draw degrades honestly when the data
 *  behind it is absent (./RawFileModal). SCAN is deliberately unreachable —
 *  no importer, table or endpoint anywhere in the tree produces a wall-loss
 *  grid, so no file can be classified as one (./emptyStates
 *  SCAN_GRID_NOT_CAPTURED). */
export function rawKindOf(file: AssetFileRow): RawFileKind {
  const ext = (file.filename.split(".").pop() ?? "").toLowerCase();
  if (file.kind === "photo" || ["jpg", "jpeg", "png", "heic", "webp"].includes(ext)) return "PHOTOS";
  if (["csv", "tsv"].includes(ext)) return "CSV";
  if (ext === "pdf") return "PDF";
  return "DOC";
}

/** The chart's source: the asset's MINIMUM reading per inspection, oldest
 *  first. `min_reading_mm` is the server's per-inspection minimum — the asset
 *  is governed by its thinnest reading and never by a mean
 *  (design_handoff/README.md:342) — so nothing is reduced here beyond dropping
 *  the inspections that carried no reading at all. */
export function thicknessSeries(record: AssetIntegrity): AssetThicknessSeries {
  return {
    points: oldestFirst(record.inspections)
      .filter((e) => e.min_reading_mm != null)
      .map((e) => ({ date: e.date, mm: e.min_reading_mm as number })),
    t_min_mm: record.governing_t_min_mm,
    governing_rate_mm_yr: record.rates.governing,
    remaining_life_years: record.rates.remaining_life_years,
  };
}

const MICRO = {
  fontSize: 8.5, fontWeight: 500, letterSpacing: ".14em", color: "var(--ink-4)",
} as const;

export function AssetIntegrityView({
  record, asset, sidebarAssets = [], dueByAsset, sidebarLoading, onSelectAsset,
  files = [], recommendations = [], repairs = [],
}: {
  record: AssetIntegrity;
  /** The open asset's row — the four facts the record does not carry, plus the
   *  tag the modal stamps on a file. */
  asset: AssetIdentityFacts & { id: number };
  sidebarAssets?: readonly SidebarAsset[];
  dueByAsset?: ReadonlyMap<number, SidebarDue>;
  sidebarLoading?: boolean;
  onSelectAsset?: (assetId: number) => void;
  files?: readonly AssetFileRow[];
  /** No writer exists for either (server/models.py:1108, :1121) — passed
   *  through so the row rendering is written and tested before the writer is.
   *  See ./emptyStates. */
  recommendations?: readonly AssetRecommendationRow[];
  repairs?: readonly AssetRepairRow[];
}): JSX.Element {
  // Which history row the detail panel is showing, and which file is open on
  // top of it. Both null = the current inspection, no modal.
  const [openedDate, setOpenedDate] = useState<string | null>(null);
  const [openFileId, setOpenFileId] = useState<number | null>(null);

  // The inspection the panel is showing — by DATE, never by position.
  const openEvent = record.inspections.find((e) => e.date === openedDate)
    ?? currentEvent(record.inspections);

  const fileLabels: FileLabel[] = files.map((f) => ({ id: f.id, name: f.filename, kind: f.kind }));
  const openFile = openFileId == null ? undefined : files.find((f) => f.id === openFileId);

  return (
    // position: relative — the raw-file modal is absolutely positioned inside
    // this row so it covers the asset and not the whole application shell
    // (README.md:157).
    <div data-testid="asset-integrity-view"
         style={{ position: "relative", display: "flex", flex: 1, minHeight: 0, minWidth: 0 }}>

      <AssetSidebar assets={sidebarAssets} dueByAsset={dueByAsset}
                    activeAssetId={asset.id} onSelect={onSelectAsset}
                    loading={sidebarLoading}
                    emptyHint="No sibling assets on file." />

      <div data-testid="asset-detail-column"
           style={{ flex: 1, minWidth: 0, overflow: "auto", padding: "17px 22px 30px" }}>

        <div className="font-mono" style={{ ...MICRO, color: "var(--accent-deep)" }}>
          THE ASSET, NOT THE JOB · EVERY INSPECTION WE HAVE EVER SIGNED ON IT
        </div>
        <h1 style={{ fontSize: 21, fontWeight: 500, color: "var(--ink-1)",
                     margin: "4px 0 0", letterSpacing: "-.015em" }}>
          Asset history
        </h1>

        <AssetIdentityBlock asset={asset} record={record} />
        <RateStrip record={record} />
        <HistoryExplainer record={record} />

        <section data-testid="thickness-chart" style={{ marginTop: 16 }}>
          <div className="font-mono" style={{ ...MICRO, marginBottom: 7 }}>
            THICKNESS · THE MINIMUM READING OF EACH INSPECTION
          </div>
          <RemainingLifeProjection asset={thicknessSeries(record)}
                                   nextDue={record.due.in_force?.due ?? null} />
        </section>

        <InspectionHistoryTable record={record} openedDate={openedDate}
                                onOpen={(d) => setOpenedDate(d === openedDate ? null : d)} />

        <InspectionDetailPanel record={record} openedDate={openedDate}
                               onOpen={setOpenedDate}
                               onOpenFile={setOpenFileId}
                               fileLabels={fileLabels} />

        <ComponentsTable record={record} />
        <AssetBottomCards record={record} recommendations={recommendations} repairs={repairs} />
      </div>

      {openFile && (
        <RawFileModal
          file={{
            id: openFile.id,
            filename: openFile.filename,
            kind: rawKindOf(openFile),
            assetTag: asset.tag,
            reportRef: openEvent?.source_ref ?? "",
          }}
          // Whose signature the file sits under. Empty = an unsigned draft,
          // and the modal refuses the download itself — this file does not
          // decide that, it only reports who signed.
          signer={openEvent?.signer ?? ""}
          onClose={() => setOpenFileId(null)}
          onDownload={openFile.download_url
            ? () => window.open(openFile.download_url as string, "_blank", "noopener")
            : undefined}
        />
      )}
    </div>
  );
}
